import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = "https://erp-xe9u.onrender.com/api";

const ROW_COLORS = [
  { badge: "bg-sky-400/10 text-sky-300 ring-sky-400/20", dot: "bg-sky-400" },
  {
    badge: "bg-cyan-400/10 text-cyan-300 ring-cyan-400/20",
    dot: "bg-cyan-400",
  },
  {
    badge: "bg-indigo-400/10 text-indigo-300 ring-indigo-400/20",
    dot: "bg-indigo-400",
  },
  {
    badge: "bg-violet-400/10 text-violet-300 ring-violet-400/20",
    dot: "bg-violet-400",
  },
  {
    badge: "bg-teal-400/10 text-teal-300 ring-teal-400/20",
    dot: "bg-teal-400",
  },
  {
    badge: "bg-amber-400/10 text-amber-300 ring-amber-400/20",
    dot: "bg-amber-400",
  },
];

const rowColor = (idx) => ROW_COLORS[idx % ROW_COLORS.length];

const newRow = () => ({
  _id: `local_${Date.now()}_${Math.random()}`,
  name: "",
  hourlyRate: 0,
  isUnion: false,
  unionName: "", // title of the linked union
  unionLevel: "1",
});

// ─── Spinner ───────────────────────────────────────────────────────────────
const Spinner = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    />
  </svg>
);

// ─── Toggle ────────────────────────────────────────────────────────────────
const Toggle = ({ value, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!value)}
    disabled={disabled}
    className={`relative w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none
                disabled:opacity-40 disabled:cursor-not-allowed shrink-0
                ${value ? "bg-orange-400" : "bg-gray-600"}`}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow
                      transition-transform duration-200 ${value ? "translate-x-4" : "translate-x-0"}`}
    />
  </button>
);

// ─── Apply confirmation ────────────────────────────────────────────────────
function ApplyConfirmDialog({ row, onConfirm, onCancel, loading }) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-orange-400/15 flex items-center justify-center shrink-0">
            <svg
              className="w-5 h-5 text-orange-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">
              Apply Rate to All Users
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              This will overwrite existing pay rates
            </p>
          </div>
        </div>
        <p className="text-sm text-gray-300 mb-5 leading-relaxed">
          Set <span className="font-semibold text-white">"{row.name}"</span> pay
          rate to{" "}
          <span className="font-semibold text-white">
            ${Number(row.hourlyRate).toFixed(2)}/hr
          </span>{" "}
          for{" "}
          <span className="text-orange-400 font-medium">
            all matching employees
          </span>
          ?
          {row.isUnion && row.unionName && (
            <span className="block text-xs text-gray-500 mt-1">
              Only <span className="text-gray-400">{row.unionName}</span>{" "}
              members at Level {row.unionLevel} will be updated.
            </span>
          )}
          <span className="block text-xs text-gray-500 mt-1">
            Existing pay rates for these users will be overwritten.
          </span>
        </p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600
                       rounded-md border border-gray-600 transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white
                       bg-orange-400 hover:bg-orange-500 rounded-md transition-colors shadow-sm disabled:opacity-40"
          >
            {loading && <Spinner />}
            {loading ? "Applying…" : "Apply to All"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete confirmation ───────────────────────────────────────────────────
function DeleteConfirmDialog({ name, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full max-w-sm p-6">
        <h3 className="text-sm font-semibold text-white mb-2">
          Remove Classification
        </h3>
        <p className="text-sm text-gray-300 mb-5">
          Remove <span className="font-semibold text-white">"{name}"</span> from
          the labor rate table? Existing users'{" "}
          <code className="text-[11px] bg-gray-700 px-1 rounded">
            workerClassification
          </code>{" "}
          will remain unchanged.
        </p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600
                       rounded-md border border-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-semibold text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Read-only summary ─────────────────────────────────────────────────────
function LaborRatesReadOnly({ rates }) {
  if (rates.length === 0) {
    return (
      <div className="bg-gray-800 border border-dashed border-gray-700 rounded-xl px-6 py-8 text-center">
        <p className="text-sm text-gray-500">
          No labor rate classifications defined yet.
        </p>
        <p className="text-xs text-gray-600 mt-1">
          Click "Edit Rates" above to add classifications.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-lg">
      <div className="grid grid-cols-[2fr_1fr_1fr] border-b border-gray-700 bg-gray-800/80">
        <div className="px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
          Classification
        </div>
        <div className="px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
          Hourly Rate
        </div>
        <div className="px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
          Union
        </div>
      </div>
      {rates.map((row, idx) => {
        const c = rowColor(idx);
        return (
          <div
            key={row._id || idx}
            className={`grid grid-cols-[2fr_1fr_1fr] items-center
              ${idx < rates.length - 1 ? "border-b border-gray-700/60" : ""}`}
          >
            <div className="px-5 py-3.5">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs
                                font-semibold ring-1 ring-inset ${c.badge}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                {row.name}
              </span>
            </div>
            <div className="px-5 py-3.5">
              {row.hourlyRate > 0 ? (
                <span className="text-sm font-medium text-gray-200">
                  ${Number(row.hourlyRate).toFixed(2)}
                  <span className="text-xs text-gray-500">/hr</span>
                </span>
              ) : (
                <span className="text-xs text-gray-600">—</span>
              )}
            </div>
            <div className="px-5 py-3.5">
              {row.isUnion && row.unionName ? (
                <div className="flex flex-col gap-0.5">
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px]
                                   font-semibold ring-1 ring-inset bg-blue-400/10 text-blue-300 ring-blue-400/20 w-fit"
                  >
                    {row.unionName}
                  </span>
                  <span className="text-[10px] text-gray-500 pl-0.5">
                    Level {row.unionLevel}
                  </span>
                </div>
              ) : (
                <span className="text-xs text-gray-600">Non-union</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────
// Props:
//   setDialog  — shared dialog handler from CompanyProfileManagement
//   isEditing  — true when Edit Rates is active
//   onClose    — callback to exit edit mode
//   unions     — array of union objects from the company profile ({ _id, title, ... })
export default function LaborRatesManagement({
  setDialog,
  isEditing,
  onClose,
  unions = [],
}) {
  const [rates, setRates] = useState([]);
  const [savedRates, setSavedRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [applyState, setApplyState] = useState({
    row: null,
    loading: false,
    confirm: false,
  });
  const [deleteState, setDeleteState] = useState({ id: null, name: "" });
  const [applySuccess, setApplySuccess] = useState({});

  const token = () => localStorage.getItem("token");
  const headers = () => ({ Authorization: `Bearer ${token()}` });

  // ── Fetch ──
  const fetchRates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/company-profile/labor-rates`, {
        headers: headers(),
      });
      const data = Array.isArray(res.data?.laborRates)
        ? res.data.laborRates
        : [];
      setRates(data);
      setSavedRates(data);
    } catch (err) {
      console.error("Error fetching labor rates:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  // Reset unsaved edits when edit mode closes
  useEffect(() => {
    if (!isEditing) setRates(savedRates);
  }, [isEditing]);

  const isDirty = JSON.stringify(rates) !== JSON.stringify(savedRates);

  // ── Helpers ──
  const updateRow = (id, field, value) =>
    setRates((prev) =>
      prev.map((r) => (r._id === id ? { ...r, [field]: value } : r)),
    );

  // When toggling union on/off, clear unionName + reset level
  const handleUnionToggle = (id, value) => {
    setRates((prev) =>
      prev.map((r) =>
        r._id === id
          ? {
              ...r,
              isUnion: value,
              unionName: value ? r.unionName : "",
              unionLevel: "1",
            }
          : r,
      ),
    );
  };

  const handleAddRow = () => setRates((prev) => [...prev, newRow()]);

  const handleRemoveClick = (row) => {
    if (String(row._id).startsWith("local_")) {
      setRates((prev) => prev.filter((r) => r._id !== row._id));
    } else {
      setDeleteState({ id: row._id, name: row.name });
    }
  };

  const handleDeleteConfirm = () => {
    setRates((prev) => prev.filter((r) => r._id !== deleteState.id));
    setSavedRates((prev) => prev.filter((r) => r._id !== deleteState.id));
    setDeleteState({ id: null, name: "" });
  };

  // ── Validate ──
  const validate = () => {
    for (const r of rates) {
      if (!r.name.trim()) return "All classifications must have a name.";
      if (parseFloat(r.hourlyRate) < 0) return "Rates cannot be negative.";
      if (r.isUnion && !r.unionName) return `Select a union for "${r.name}".`;
    }
    const names = rates.map((r) => r.name.trim().toLowerCase());
    const dupes = names.filter((n, i) => names.indexOf(n) !== i);
    if (dupes.length) return `Duplicate classification name: "${dupes[0]}"`;
    return null;
  };

  // ── Save ──
  const handleSave = async () => {
    const err = validate();
    if (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Validation Error",
        message: err,
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
      return;
    }
    setSaving(true);
    try {
      const parsed = rates.map((r) => ({
        ...(!String(r._id).startsWith("local_") && { _id: r._id }),
        name: r.name.trim(),
        hourlyRate: parseFloat(r.hourlyRate) || 0,
        isUnion: !!r.isUnion,
        unionName: r.isUnion ? r.unionName : "",
        unionLevel: r.isUnion ? r.unionLevel || "1" : null,
      }));
      const res = await axios.put(
        `${API}/company-profile/labor-rates`,
        { rates: parsed },
        { headers: headers() },
      );
      const fresh = Array.isArray(res.data) ? res.data : [];
      setRates(fresh);
      setSavedRates(fresh);
      setDialog({
        open: true,
        type: "success",
        title: "Saved",
        message: "Labor rates saved successfully.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to save labor rates.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Apply ──
  const handleApplyClick = (row) => {
    if (isDirty) {
      setDialog({
        open: true,
        type: "error",
        title: "Unsaved Changes",
        message: "Please save your changes before applying rates to users.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
      return;
    }
    setApplyState({ row, loading: false, confirm: true });
  };

  const handleApplyConfirm = async () => {
    const { row } = applyState;
    setApplyState((p) => ({ ...p, loading: true }));
    try {
      const res = await axios.post(
        `${API}/company-profile/labor-rates/apply`,
        {
          classificationName: row.name,
          isUnion: row.isUnion,
          unionName: row.unionName,
          unionLevel: row.unionLevel,
        },
        { headers: headers() },
      );
      setApplyState({ row: null, loading: false, confirm: false });
      setApplySuccess((prev) => ({ ...prev, [row._id]: true }));
      setTimeout(
        () => setApplySuccess((prev) => ({ ...prev, [row._id]: false })),
        3000,
      );
      setDialog({
        open: true,
        type: "success",
        title: "Rates Applied",
        message: res.data.message,
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setApplyState((p) => ({ ...p, loading: false, confirm: false }));
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to apply rates.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  const baseInput =
    "block w-full rounded-md bg-gray-700 border border-gray-600 text-gray-100 text-sm " +
    "px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 transition-colors";
  const rateInput =
    "block w-full rounded-md bg-gray-700 border border-gray-600 text-gray-100 text-sm " +
    "pl-6 pr-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 transition-colors";
  const selectClass =
    "block w-full rounded-md bg-gray-700 border border-gray-600 text-gray-100 text-sm " +
    "px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 transition-colors";

  if (loading) {
    return (
      <div className="mt-8 border-t border-gray-700 pt-8">
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Spinner className="w-4 h-4" /> Loading labor rates…
        </div>
      </div>
    );
  }

  return (
    <>
      {applyState.confirm && (
        <ApplyConfirmDialog
          row={applyState.row}
          onConfirm={handleApplyConfirm}
          onCancel={() =>
            setApplyState({ row: null, loading: false, confirm: false })
          }
          loading={applyState.loading}
        />
      )}
      {deleteState.id && (
        <DeleteConfirmDialog
          name={deleteState.name}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteState({ id: null, name: "" })}
        />
      )}

      <div className="mt-8 border-t border-gray-700 pt-8">
        {/* ── Header ── */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-semibold text-white">Labor Rates</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {isEditing
                ? "Add or edit worker classifications with standard hourly rates."
                : "Standard pay rates by worker classification."}
            </p>
          </div>
          {isEditing && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleAddRow}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-300
                           bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Classification
              </button>
              <button
                onClick={handleSave}
                disabled={!isDirty || saving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white
                           bg-orange-400 hover:bg-orange-500 rounded-lg transition-colors shadow-sm
                           disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving && <Spinner />}
                {saving ? "Saving…" : "Save Rates"}
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600
                           rounded-lg border border-gray-600 transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>

        {/* ── Read-only view ── */}
        {!isEditing && <LaborRatesReadOnly rates={savedRates} />}

        {/* ── Edit view ── */}
        {isEditing && (
          <>
            {rates.length === 0 ? (
              <div className="bg-gray-800 border border-dashed border-gray-700 rounded-xl px-6 py-10 text-center">
                <svg
                  className="w-8 h-8 text-gray-600 mx-auto mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857
                       M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857
                       m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <p className="text-sm text-gray-500 mb-3">
                  No classifications defined yet.
                </p>
                <button
                  onClick={handleAddRow}
                  className="text-xs text-orange-400 hover:text-orange-300 transition-colors"
                >
                  Add your first classification →
                </button>
              </div>
            ) : (
              <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-lg">
                {/* Column headers */}
                <div className="grid grid-cols-[2fr_120px_1fr_auto] border-b border-gray-700 bg-gray-800/80">
                  <div className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Classification
                  </div>
                  <div className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Hourly Rate
                  </div>
                  <div className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Union
                  </div>
                  <div className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider text-right">
                    Actions
                  </div>
                </div>

                {/* Rows */}
                {rates.map((row, idx) => {
                  const c = rowColor(idx);
                  const isLocal = String(row._id).startsWith("local_");
                  const saved = savedRates.find((r) => r._id === row._id);
                  const nameDirty = row.name !== saved?.name;
                  const hDirty = row.hourlyRate !== saved?.hourlyRate;
                  const anyDirty = nameDirty || hDirty || isLocal;
                  const applied = applySuccess[row._id];

                  return (
                    <div
                      key={row._id}
                      className={`grid grid-cols-[2fr_120px_1fr_auto] items-start py-3.5
                        ${idx < rates.length - 1 ? "border-b border-gray-700/60" : ""}
                        ${anyDirty ? "bg-orange-400/[0.02]" : "hover:bg-gray-700/20"} transition-colors`}
                    >
                      {/* ── Classification name ── */}
                      <div className="px-4 flex flex-col gap-1.5">
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-2 h-2 rounded-full shrink-0 ${c.dot}`}
                          />
                          <input
                            type="text"
                            value={row.name}
                            onChange={(e) =>
                              updateRow(row._id, "name", e.target.value)
                            }
                            placeholder="e.g. Apprentice, Journeyman…"
                            className={`${baseInput} ${
                              (nameDirty || isLocal) && row.name
                                ? "border-orange-400/50 bg-orange-400/5"
                                : isLocal
                                  ? "border-orange-400/30"
                                  : ""
                            }`}
                          />
                        </div>
                        {anyDirty && (
                          <p className="text-[10px] text-orange-400 pl-4">
                            Unsaved
                          </p>
                        )}
                      </div>

                      {/* ── Hourly rate ── */}
                      <div className="px-4">
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm select-none">
                            $
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.hourlyRate}
                            onChange={(e) =>
                              updateRow(row._id, "hourlyRate", e.target.value)
                            }
                            className={`${rateInput} ${hDirty ? "border-orange-400/50 bg-orange-400/5" : ""}`}
                            placeholder="0.00"
                          />
                        </div>
                      </div>

                      {/* ── Union column ── */}
                      <div className="px-4 flex flex-col gap-2">
                        {/* Toggle row */}
                        <div className="flex items-center gap-2 pt-1">
                          <Toggle
                            value={!!row.isUnion}
                            onChange={(v) => handleUnionToggle(row._id, v)}
                          />
                          <span className="text-xs text-gray-400">
                            {row.isUnion ? "Union" : "Non-union"}
                          </span>
                        </div>

                        {/* Union dropdown + level pills — shown when toggled on */}
                        {row.isUnion && (
                          <div className="flex flex-col gap-1.5">
                            {/* Union selector */}
                            <select
                              value={row.unionName || ""}
                              onChange={(e) =>
                                updateRow(row._id, "unionName", e.target.value)
                              }
                              className={`${selectClass} ${!row.unionName ? "border-amber-400/40 bg-amber-400/5" : ""}`}
                            >
                              <option value="">Select union…</option>
                              {unions.map((u) => (
                                <option key={u._id} value={u.title}>
                                  {u.title}
                                </option>
                              ))}
                            </select>

                            {/* Level pills */}
                            {row.unionName && (
                              <div className="flex items-center gap-1.5">
                                <span className="text-[11px] text-gray-500 shrink-0">
                                  Level
                                </span>
                                {["0", "1"].map((lvl) => (
                                  <button
                                    key={lvl}
                                    type="button"
                                    onClick={() =>
                                      updateRow(row._id, "unionLevel", lvl)
                                    }
                                    className={`px-2.5 py-0.5 rounded-md text-xs font-semibold border transition-colors
                                      ${
                                        row.unionLevel === lvl
                                          ? "bg-orange-400 text-white border-orange-400 shadow-sm"
                                          : "bg-gray-700 text-gray-400 border-gray-600 hover:border-orange-400/40 hover:text-gray-200"
                                      }`}
                                  >
                                    {lvl}
                                  </button>
                                ))}
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded text-[10px]
                                                 font-semibold ring-1 ring-inset bg-blue-400/10 text-blue-300 ring-blue-400/20"
                                >
                                  {row.unionName} · Lvl {row.unionLevel}
                                </span>
                              </div>
                            )}

                            {!row.unionName && (
                              <p className="text-[10px] text-amber-400">
                                Select a union to continue
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* ── Actions ── */}
                      <div className="px-4 flex flex-col items-end gap-1.5 pt-1">
                        <button
                          onClick={() => handleApplyClick(row)}
                          disabled={isLocal || (row.isUnion && !row.unionName)}
                          title={
                            isLocal
                              ? "Save first to apply"
                              : row.isUnion && !row.unionName
                                ? "Select a union first"
                                : `Apply $${Number(row.hourlyRate).toFixed(2)}/hr to all "${row.name}" users`
                          }
                          className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold
                                      border transition-all whitespace-nowrap disabled:opacity-30 disabled:cursor-not-allowed
                                      ${
                                        applied
                                          ? "bg-green-400/15 text-green-300 border-green-400/30"
                                          : "bg-gray-700 hover:bg-orange-400/10 text-gray-300 hover:text-orange-300 border-gray-600 hover:border-orange-400/30"
                                      }`}
                        >
                          {applied ? (
                            <>
                              <svg
                                className="w-3 h-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2.5}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              Applied
                            </>
                          ) : (
                            <>
                              <svg
                                className="w-3 h-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857
                                   M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857
                                   m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                              </svg>
                              Apply to All
                            </>
                          )}
                        </button>

                        <button
                          onClick={() => handleRemoveClick(row)}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold
                                     border border-gray-700 text-gray-600 hover:text-red-400
                                     hover:border-red-400/30 hover:bg-red-400/5 transition-all whitespace-nowrap"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7
                                 m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <p className="text-xs text-gray-600 mt-3 leading-relaxed">
              The hourly rate is applied to{" "}
              <code className="text-[10px] bg-gray-700/60 px-1 rounded">
                payRate
              </code>{" "}
              for all users with this classification, regardless of pay type.
              When union is on, selecting a union also auto-fills the user's{" "}
              <code className="text-[10px] bg-gray-700/60 px-1 rounded">
                union
              </code>{" "}
              and{" "}
              <code className="text-[10px] bg-gray-700/60 px-1 rounded">
                level
              </code>{" "}
              fields on creation. New rows must be{" "}
              <span className="text-gray-500">saved</span> before Apply is
              available.
            </p>
          </>
        )}
      </div>
    </>
  );
}
